import {FC, useRef, useState} from 'react'
import Slider, {Settings} from 'react-slick'

import {SLIDER_TRANSITION} from '@/theme/cssTransitions'
import {CONTAINER_PADDING_MOBILE} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {
  MOBILE_MAX_WIDTH,
  MOBILE_SCREENSIZE,
  TABLET_MAX_WIDTH,
  TABLET_SCREENSIZE,
  USER_TABLET_MAX_WIDTH,
} from '@/utils/styles/breakpoints'

import {Text, TextVariant} from '../../common/content/Text'
import {ArrowButton} from '../ArrowButton'
import {TopicCard} from './TopicCard'

const settings: Settings = {
  // Infinite mode clones every slide into the HTML, repeating each topic heading and link.
  infinite: false,
  speed: SLIDER_TRANSITION,
  slidesToShow: 4,
  slidesToScroll: 1,
  autoplay: false,
  arrows: false,
  responsive: [
    {
      breakpoint: TABLET_MAX_WIDTH,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: USER_TABLET_MAX_WIDTH,
      settings: {
        slidesToShow: 2,
      },
    },
    {
      breakpoint: MOBILE_MAX_WIDTH,
      settings: {
        slidesToShow: 1,
        dots: true,
        centerMode: true,
      },
    },
  ],
}

type TopicsCarouselProps = {
  title: string
  topics: {
    title: string
    linkHref: string
    description?: string
  }[]
}

export const TopicsCarousel: FC<React.PropsWithChildren<TopicsCarouselProps>> = ({
  title,
  topics,
}) => {
  const sliderRef = useRef<Slider>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)

  // slick marks the slides in view `slick-active`, and knows how many fit at each breakpoint.
  const updateArrows = () => {
    const slides = carouselRef.current?.querySelectorAll('.slick-slide')
    setIsAtStart(slides?.[0]?.classList.contains('slick-active') ?? true)
    setIsAtEnd(slides?.[slides.length - 1]?.classList.contains('slick-active') ?? false)
  }

  return (
    <>
      <div className="topics">
        <Text variant={TextVariant.h2} align="center">
          {title}
        </Text>
        <div className="topics-carousel carousel" ref={carouselRef}>
          <Slider
            {...settings}
            ref={sliderRef}
            onInit={updateArrows}
            onReInit={updateArrows}
            afterChange={updateArrows}
          >
            {topics.map(({title, linkHref, description}) => (
              <TopicCard
                key={title}
                linkHref={linkHref}
                title={title}
                description={description}
                isCentered
              />
            ))}
          </Slider>
        </div>
        <div className="buttons">
          <div className="button">
            <ArrowButton
              variant="left"
              onClick={() => sliderRef?.current?.slickPrev()}
              disabled={isAtStart}
            />
          </div>
          <div className="button">
            <ArrowButton onClick={() => sliderRef?.current?.slickNext()} disabled={isAtEnd} />
          </div>
        </div>
      </div>

      <style jsx>{`
        .topics-carousel {
          margin-top: ${theme.spacing[6]};
        }

        .buttons {
          display: flex;
          justify-content: flex-end;
          margin-top: ${theme.spacing[5]};
        }

        .button + .button {
          margin-left: ${theme.spacing[4]};
        }

        @media ${TABLET_SCREENSIZE} {
          .topics {
            margin-top: ${theme.spacing[5]};
          }

          .topics-carousel {
            margin-top: ${theme.spacing[5]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .topics {
            margin-top: ${theme.spacing[2]};
          }

          .buttons {
            display: none;
          }
        }
      `}</style>

      {/* Please, use global styles only if you need to override CSS from external libraries */}
      <style jsx global>{`
        .topics-carousel.carousel .slick-slider {
          max-width: inherit;
        }

        .topics-carousel .slick-track {
          display: flex;
        }

        .topics-carousel .slick-list {
          margin: 0 -${theme.spacing[2]};
        }

        .topics-carousel .slick-slide {
          display: flex;
          height: inherit;
          padding: 0 ${theme.spacing[2]};
        }

        .topics-carousel .slick-slide > div {
          width: 100%;
        }

        @media ${MOBILE_SCREENSIZE} {
          .topics-carousel .slick-list {
            margin: 0 -${CONTAINER_PADDING_MOBILE};
          }

          .topics-carousel .slick-slide {
            padding: 0 ${theme.spacing[1]};
          }
        }
      `}</style>
    </>
  )
}
